import React, { useState, useRef, useEffect } from 'react';
import { Box, Divider, Typography, Switch, Tooltip, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  TableRowsOutlined, AddOutlined, DeleteOutlineOutlined,
  DragIndicatorOutlined, CheckOutlined,
} from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { InspectorSection, StyleRow, StyleInput, ColorInput, PanelHeader } from '../ui/primitives';
import { LayoutModeBanner } from './LayoutModeBanner';
import { MediaField } from '../../lobbyBuilderTab/MediaField';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 9); }

function SectionDivider() {
  return <Divider sx={{ borderColor: '#f0f2f5', my: 0.5 }} />;
}

const selectSx = {
  border: '1px solid #e4e9f0', borderRadius: '6px',
  px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit',
  background: '#f8f9fb', color: '#1a1a2e', outline: 'none',
  cursor: 'pointer', width: '100%',
  '&:focus': { borderColor: PRIMARY },
};

// ─── Template picker ──────────────────────────────────────────────────────────
function TemplatePicker({ value, onChange }) {
  const templates = [
    {
      key: 'columns',
      label: 'Three Columns',
      desc: 'Logo · Links · Copyright',
      preview: (
        <Box sx={{ width: '100%', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, background: '#ffffff', border: '1px solid #e8edf2', borderRadius: '4px', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: alpha(PRIMARY, 0.5) }} />
            <Box sx={{ width: 22, height: 4, borderRadius: 0.5, background: '#d4dbe6' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[18, 18, 18].map((w, i) => <Box key={i} sx={{ width: w, height: 3, borderRadius: 0.5, background: '#e4e9f0' }} />)}
          </Box>
          <Box sx={{ width: 32, height: 3, borderRadius: 0.5, background: '#eaecf0' }} />
        </Box>
      ),
    },
    {
      key: 'centered',
      label: 'Centered Stack',
      desc: 'Logo · Links · Copyright stacked',
      preview: (
        <Box sx={{ width: '100%', height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 1.5, background: '#f8f9fb', border: '1px solid #e8edf2', borderRadius: '4px', gap: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: alpha(PRIMARY, 0.5) }} />
            <Box sx={{ width: 22, height: 4, borderRadius: 0.5, background: '#d4dbe6' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.6 }}>
            {[18, 18, 18, 18].map((w, i) => <Box key={i} sx={{ width: w, height: 3, borderRadius: 0.5, background: '#e4e9f0' }} />)}
          </Box>
          <Box sx={{ width: 52, height: 3, borderRadius: 0.5, background: '#eaecf0' }} />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {templates.map(t => {
        const active = value === t.key;
        return (
          <Box key={t.key} onClick={() => onChange(t.key)}
            sx={{ width: '100%', cursor: 'pointer', borderRadius: 1.5, p: 1, border: active ? `2px solid ${PRIMARY}` : '2px solid #e8edf2', background: active ? alpha(PRIMARY, 0.03) : '#fafbfc', transition: 'all 0.15s', '&:hover': { borderColor: alpha(PRIMARY, 0.4) } }}
          >
            {t.preview}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 600, color: active ? PRIMARY : '#5a6474' }}>{t.label}</Typography>
                <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3', mt: 0.1 }}>{t.desc}</Typography>
              </Box>
              {active && <CheckOutlined sx={{ fontSize: 13, color: PRIMARY }} />}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Link type options ────────────────────────────────────────────────────────
const LINK_TYPE_OPTIONS = [
  { value: 'external', label: 'External URL' },
  { value: 'section',  label: 'Page section'  },
  { value: 'page',     label: 'Another page'  },
];

// ─── Inline-editable label ────────────────────────────────────────────────────
function InlineLabel({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <Box component="input" ref={inputRef} value={draft}
        onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        onClick={e => e.stopPropagation()}
        sx={{ flex: 1, border: 'none', borderBottom: `1px solid ${PRIMARY}`, outline: 'none', background: 'transparent', fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e', fontFamily: 'inherit', p: 0, minWidth: 0 }}
      />
    );
  }

  return (
    <Typography onDoubleClick={e => { e.stopPropagation(); setEditing(true); }} title="Double-click to rename"
      sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text', minWidth: 0 }}>
      {value || 'Untitled link'}
    </Typography>
  );
}

// ─── Single link row ──────────────────────────────────────────────────────────
function LinkRow({ link, allPages, allSections, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const update = (patch) => onChange({ ...link, ...patch });

  return (
    <Box sx={{ border: '1px solid #e8edf2', borderRadius: 1.5, overflow: 'hidden', background: expanded ? '#fafbfc' : '#fff', transition: 'background 0.1s' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.7 }}>
        <DragIndicatorOutlined sx={{ fontSize: 13, color: '#d4dbe6', cursor: 'grab', flexShrink: 0 }} />
        <InlineLabel value={link.label || ''} onChange={v => update({ label: v })} />
        <Box onClick={() => setExpanded(v => !v)}
          sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 0.75, cursor: 'pointer', color: '#c0c8d4', transition: 'all 0.1s', '&:hover': { background: '#f0f4f8', color: '#7a8494' } }}>
          <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: expanded ? 'none' : '5px solid currentColor', borderBottom: expanded ? '5px solid currentColor' : 'none', mt: expanded ? 0 : '2px', mb: expanded ? '2px' : 0 }} />
        </Box>
        <Tooltip title="Remove link">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(); }}
            sx={{ p: 0.3, flexShrink: 0, color: '#c0c8d4', '&:hover': { color: '#dc2626' } }}>
            <DeleteOutlineOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {expanded && (
        <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: '1px solid #f0f2f5' }}>
          <StyleRow label="Type">
            <Box component="select" value={link.type || 'external'} onChange={e => update({ type: e.target.value, href: '' })} sx={selectSx}>
              {LINK_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Box>
          </StyleRow>

          {(!link.type || link.type === 'external') && (
            <StyleRow label="URL">
              <StyleInput value={link.href || ''} onChange={v => update({ href: v })} placeholder="https://…" />
            </StyleRow>
          )}
          {link.type === 'section' && (
            <StyleRow label="Section">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a section —</option>
                {allSections.map(s => <option key={s.id} value={`#section-${s.id}`}>{s.label || 'Untitled section'}</option>)}
              </Box>
            </StyleRow>
          )}
          {link.type === 'page' && (
            <StyleRow label="Page">
              <Box component="select" value={link.href || ''} onChange={e => update({ href: e.target.value })} sx={selectSx}>
                <option value="">— pick a page —</option>
                {allPages.map(p => <option key={p._id} value={`/${p.slug}`}>{p.title}</option>)}
              </Box>
            </StyleRow>
          )}
          {(!link.type || link.type === 'external') && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#7a8494' }}>Open in new tab</Typography>
              <Switch checked={!!link.newTab} onChange={e => update({ newTab: e.target.checked })} size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY } }} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Links list ───────────────────────────────────────────────────────────────
function LinksEditor({ links, allPages, allSections, onChange }) {
  const addLink    = () => onChange([...links, { id: uid(), label: 'New Link', type: 'external', href: '', newTab: false }]);
  const updateLink = (i, patch) => onChange(links.map((l, idx) => idx === i ? patch : l));
  const deleteLink = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <InspectorSection label="Footer Links">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {links.length === 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: '#c0c8d4', textAlign: 'center', py: 1, fontStyle: 'italic' }}>No links yet</Typography>
        )}
        {links.map((link, i) => (
          <LinkRow key={link.id || i} link={link} allPages={allPages} allSections={allSections}
            onChange={patch => updateLink(i, patch)} onDelete={() => deleteLink(i)} />
        ))}
        <Box onClick={addLink}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, py: 0.7, borderRadius: 1.5, border: '1px dashed #c4cdd8', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: '#9ea8b3', mt: 0.25, transition: 'all 0.15s', '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.04) } }}>
          <AddOutlined sx={{ fontSize: 14 }} />
          Add link
        </Box>
      </Box>
    </InspectorSection>
  );
}

// ─── Color row ────────────────────────────────────────────────────────────────
function ColorRow({ label, value, onChange }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#7a8494', mb: 0.4, fontWeight: 500 }}>{label}</Typography>
      <ColorInput value={value} onChange={onChange} />
    </Box>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function FooterInspector({
  section, onUpdate, onClose, allPages, allSections,
  isOverride, onEnableOverride, onDisableOverride,
}) {
  const config = section.footerConfig || {};

  const updateConfig = (patch) => onUpdate({ ...section, footerConfig: { ...config, ...patch } });
  const updateStyle  = (patch) => updateConfig({ style: { ...(config.style || {}), ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<TableRowsOutlined sx={{ fontSize: 14 }} />} label="Footer" color={PRIMARY} onClose={onClose} />

      {/* ── Global / Custom mode banner ── */}
      {/* <LayoutModeBanner
        isOverride={isOverride}
        label="footer"
        onEnableOverride={onEnableOverride}
        onDisableOverride={onDisableOverride}
      /> */}

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Template">
          <TemplatePicker value={config.template || 'columns'} onChange={v => updateConfig({ template: v })} />
        </InspectorSection>

        <SectionDivider />

        <InspectorSection label="Logo">
          <StyleRow label="Logo Image">
          <MediaField value={config.logoSrc || ''} onChange={v => updateConfig({ logoSrc: v })} mediaType="image" />
        </StyleRow>
          {config.logoSrc ? (
            <>
              <StyleRow label="Alt text">
                <StyleInput value={config.logoAlt || ''} onChange={v => updateConfig({ logoAlt: v })} placeholder="Company logo" />
              </StyleRow>
              <StyleRow label="Height">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component="input" type="number" value={config.logoHeight || 32} min={16} max={80}
                    onChange={e => updateConfig({ logoHeight: Number(e.target.value) })}
                    sx={{ width: 56, border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>px</Typography>
                </Box>
              </StyleRow>
            </>
          ) : (
            <StyleRow label="Text">
              <StyleInput value={config.logoText || ''} onChange={v => updateConfig({ logoText: v })} placeholder="My Event" />
            </StyleRow>
          )}
          <StyleRow label="Tagline">
            <StyleInput value={config.tagline || ''} onChange={v => updateConfig({ tagline: v })} placeholder="Optional short description" />
          </StyleRow>
        </InspectorSection>

        <SectionDivider />

        <LinksEditor
          links={config.links || []}
          allPages={allPages || []}
          allSections={allSections || []}
          onChange={links => updateConfig({ links })}
        />

        <SectionDivider />

        <InspectorSection label="Copyright">
          <StyleRow label="Text">
            <StyleInput value={config.copyrightText || ''} onChange={v => updateConfig({ copyrightText: v })}
              placeholder={`© ${new Date().getFullYear()} My Company. All rights reserved.`} />
          </StyleRow>
        </InspectorSection>

        <SectionDivider />

        <InspectorSection label="Colors">
          <ColorRow label="Background"          value={config.style?.background     || '#ffffff'} onChange={v => updateStyle({ background: v })} />
          <ColorRow label="Logo / brand text"   value={config.style?.logoColor      || '#1a1a2e'} onChange={v => updateStyle({ logoColor: v })} />
          <ColorRow label="Link color"          value={config.style?.linkColor      || '#5a6474'} onChange={v => updateStyle({ linkColor: v })} />
          <ColorRow label="Link hover color"    value={config.style?.linkHoverColor || '#1075BA'} onChange={v => updateStyle({ linkHoverColor: v })} />
          <ColorRow label="Copyright text"      value={config.style?.copyrightColor || '#9ea8b3'} onChange={v => updateStyle({ copyrightColor: v })} />
          <ColorRow label="Top border"          value={config.style?.borderColor    || '#e8edf2'} onChange={v => updateStyle({ borderColor: v })} />
        </InspectorSection>

        <SectionDivider />

        <InspectorSection label="Spacing">
          <StyleRow label="Padding X">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box component="input" type="number" value={parseInt(config.style?.paddingX || 48)} min={0} max={200}
                onChange={e => updateStyle({ paddingX: `${e.target.value}px` })}
                sx={{ width: 56, border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>px</Typography>
            </Box>
          </StyleRow>
          <StyleRow label="Padding Y">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box component="input" type="number" value={parseInt(config.style?.paddingY || 40)} min={0} max={160}
                onChange={e => updateStyle({ paddingY: `${e.target.value}px` })}
                sx={{ width: 56, border: '1px solid #e4e9f0', borderRadius: 1, px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit', background: '#f8f9fb', outline: 'none', '&:focus': { borderColor: PRIMARY } }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>px</Typography>
            </Box>
          </StyleRow>
        </InspectorSection>

      </Box>
    </Box>
  );
}